import { Delete } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import { EntityGrid } from '@sophocles/data-grids';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { fetchAllUsers } from '@sophocles/entities/User';
import { usePatchUserMutation } from '@sophocles/features/entities/users';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import { addToast } from '@sophocles/features/toast';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

const FALLBACK_SALES_GROUP_ID = 1072;

/**
 * Sales > People grid.
 *
 * The group id is sourced from `tenantInfo.sales_group_id` with a
 * client-side fallback of 1072 (the historical hardcode). Production
 * tenants must add `sales_group_id` to their .config.json since pliny
 * replaces TENANT_CONFIG wholesale rather than merging.
 */
export default function SalesPeopleGrid() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tenantInfo = useSelector(selectTenantInfo);
  const groupId = tenantInfo?.sales_group_id ?? FALLBACK_SALES_GROUP_ID;

  const [patchUser] = usePatchUserMutation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Captured from onGridReady so post-mutation callers can refetch page 0.
  const { onGridReady, refresh: refreshGrid } = useGridRefreshRef();

  const extraParams = useMemo(() => ({ group_id: groupId }), [groupId]);

  const columnVisibility = useColumnVisibilityModel('salesPeopleGrid');

  const removeFromGroup = useCallback(
    async user => {
      const newGroupIds = user.groups.map(g => g.id).filter(id => id !== groupId);
      try {
        await patchUser({ id: user.id, body: { group_ids: newGroupIds } }).unwrap();
        refreshGrid();
      } catch (error) {
        dispatch(
          addToast({
            title: t('general.error'),
            message: error?.data?.detail || t('warehouse.salesPersonRemoveError'),
            level: 'error',
            dismissible: true,
          }),
        );
      }
    },
    [patchUser, dispatch, groupId, refreshGrid],
  );

  // Per-row delete button — bespoke action not emitted by the descriptor.
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('actions.actions'),
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <IconButton
            aria-label={t('actions.remove')}
            onClick={() => confirmDelete(row, { entityLabel: t('warehouse.salesPerson'), onConfirm: removeFromGroup })}
          >
            <Delete fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [confirmDelete, removeFromGroup, t],
  );

  useEffect(() => {
    dispatch(fetchAllUsers({ is_staff: true })({ limit: 1000, offset: 0, o: 'id' })).then(response =>
      setStaffUsers(response.toJS().results),
    );
  }, [dispatch]);

  const handleAddUserToGroup = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await patchUser({ id: selectedUser.id, body: { group_ids: [...selectedUser.groups, groupId] } }).unwrap();
      setDialogOpen(false);
      setSelectedUser(null);
      refreshGrid();
    } catch (error) {
      dispatch(
        addToast({
          title: t('general.error'),
          message: error?.data?.detail || t('warehouse.salesPersonAddError'),
          level: 'error',
          dismissible: true,
        }),
      );
    }
  }, [patchUser, dispatch, selectedUser, groupId, refreshGrid]);

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
      {deleteConfirmDialog}
      <EntityGrid
        basePath="users"
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('warehouse.searchSalesPeople'),
          persistKey: 'salesPeopleGrid',
          infiniteScroll: true,
          defaultSort: [{ field: 'id', sort: 'asc' }],
          createAction: {
            label: t('warehouse.addSalesPerson'),
            onClick: () => setDialogOpen(true),
          },
          onGridReady,
          extraColumns,
          columnVisibility,
          countNoun: t('warehouse.salesPeopleCountNoun'),
        }}
      />

      <Dialog
        fullWidth
        open={dialogOpen}
        maxWidth="md"
        onClose={() => {
          setDialogOpen(false);
          setSelectedUser(null);
        }}
        PaperProps={{ style: { minHeight: 400, maxHeight: '80vh' } }}
      >
        <DialogTitle>{t('warehouse.addSalesPerson')}</DialogTitle>
        <DialogContent>
          <UserAutoComplete
            fullWidth
            size="large"
            name="salesperson"
            users={staffUsers}
            value={selectedUser?.id}
            onChange={(_e, _value, opt) => setSelectedUser(opt)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setSelectedUser(null);
            }}
            color="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleAddUserToGroup} color="primary" disabled={!selectedUser}>
            {t('actions.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
